import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { MacroCard } from "@/components/MacroCard";
import { RequestMacroDialog } from "@/components/RequestMacroDialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Search, TrendingUp, Sparkles, X, ChevronDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { getTagColor } from "@/lib/tagColors";
import { getCategoryDescription } from "@/lib/categoryDescriptions";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useOnboarding } from "@/hooks/useOnboarding";
import { useAuth } from "@/hooks/useAuth";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "@/styles/driver-theme.css";
import { useTheme } from "next-themes";
interface Macro {
  id: string;
  slug: string;
  name: string;
  short_desc: string;
  risk_score: "low" | "medium" | "high" | "unknown";
  views_count: number;
  screenshot_url?: string | null;
  public_profiles: {
    username: string;
    full_name: string;
    verified: boolean;
    avatar_url?: string | null;
  };
  macro_tags: {
    tags: {
      id: number;
      name: string;
    };
  }[];
}
interface Tag {
  id: number;
  name: string;
}
const Macros = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasCompletedOnboarding, markOnboardingComplete } = useOnboarding();
  const { user } = useAuth();
  const { setTheme } = useTheme();
  const [macros, setMacros] = useState<Macro[]>([]);
  const [trendingMacros, setTrendingMacros] = useState<Macro[]>([]);
  const [newMacros, setNewMacros] = useState<Macro[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState<
    Array<{
      name: string;
      slug: string;
    }>
  >([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showRequestDialog, setShowRequestDialog] = useState(false);

  // Force light mode when signed out
  useEffect(() => {
    if (user === null) {
      setTheme('light');
    }
  }, [user, setTheme]);
  useEffect(() => {
    fetchTags();
    fetchFeaturedMacros();
    fetchSuggestions();
  }, []);

  // Separate effect to handle scroll restoration after macros are loaded
  useEffect(() => {
    if (!loading && macros.length > 0) {
      const clickedMacroSlug = sessionStorage.getItem("clickedMacroSlug");
      if (clickedMacroSlug) {
        setTimeout(() => {
          // Find the macro card element
          const macroElement = document.querySelector(`[data-macro-slug="${clickedMacroSlug}"]`);
          if (macroElement) {
            // Scroll to ensure the card is in view
            macroElement.scrollIntoView({
              behavior: "instant",
              block: "center",
            });
          }
          // Clear the stored slug
          sessionStorage.removeItem("clickedMacroSlug");
        }, 100);
      } else {
        // If no clicked macro, scroll to top
        window.scrollTo({
          top: 0,
          behavior: "instant",
        });
      }

      // Only show onboarding for authenticated users
      if (user) {
        const shouldStartOnboarding = localStorage.getItem('startOnboarding');
        const checkNewUser = localStorage.getItem('checkNewUser');
        
        // Start onboarding tour for new authenticated users
        if (shouldStartOnboarding === 'true' || (!hasCompletedOnboarding && !checkNewUser)) {
          localStorage.removeItem('startOnboarding');
          localStorage.removeItem('checkNewUser');
          
          setTheme("light"); // Force light mode for onboarding
          
          setTimeout(() => {
            startVaultTour();
          }, 500);
        } else if (checkNewUser === 'true') {
          // For OAuth users, check if they're actually new
          supabase
            .from('profiles')
            .select('created_at')
            .eq('id', user.id)
            .single()
            .then(({ data: profile }) => {
              if (profile) {
                const createdAt = new Date(profile.created_at);
                const now = new Date();
                const minutesSinceCreation = (now.getTime() - createdAt.getTime()) / (1000 * 60);
                
                if (minutesSinceCreation < 5 && !hasCompletedOnboarding) {
                  localStorage.removeItem('checkNewUser');
                  setTheme("light"); // Force light mode for onboarding
                  setTimeout(() => {
                    startVaultTour();
                  }, 500);
                } else {
                  localStorage.removeItem('checkNewUser');
                }
              }
            });
        }
      }
      
      // Show subtle encouragement toast for anonymous users after viewing a few macros
      if (!user && macros.length > 0) {
        const viewCount = parseInt(sessionStorage.getItem('anonymousViewCount') || '0');
        if (viewCount === 2) {
          setTimeout(() => {
            toast.info("Sign in to save your favorite macros and upload your own!");
          }, 2000);
        }
        sessionStorage.setItem('anonymousViewCount', String(viewCount + 1));
      }
    }
  }, [loading, macros, hasCompletedOnboarding, user]);
  const fetchSuggestions = async () => {
    const { data } = await supabase
      .from("macros")
      .select("name, slug")
      .eq("is_published", true)
      .order("views_count", {
        ascending: false,
      })
      .limit(5);
    if (data) {
      setSuggestions(data);
    }
  };
  useEffect(() => {
    fetchMacros();
  }, [searchQuery, selectedTag, activeFilter]);
  const fetchTags = async () => {
    const { data } = await (supabase.from as any)("tags").select("*").order("name");
    setTags(data || []);
  };
  const fetchFeaturedMacros = async () => {
    // Fetch trending (most viewed)
    const { data: trending } = await (supabase as any)
      .from("macros")
      .select(
        `
        id, slug, name, short_desc, risk_score, views_count, screenshot_url,
        public_profiles (username, full_name, verified, avatar_url),
        macro_tags (tags (id, name))
      `,
      )
      .eq("is_published", true)
      .eq("is_approved", true)
      .order("views_count", {
        ascending: false,
      })
      .limit(6);
    
    setTrendingMacros((trending || []) as Macro[]);

    // Fetch newest
    const { data: newest } = await (supabase as any)
      .from("macros")
      .select(
        `
        id, slug, name, short_desc, risk_score, views_count, screenshot_url,
        public_profiles (username, full_name, verified, avatar_url),
        macro_tags (tags (id, name))
      `,
      )
      .eq("is_published", true)
      .eq("is_approved", true)
      .order("created_at", {
        ascending: false,
      })
      .limit(6);
    
    setNewMacros((newest || []) as Macro[]);
  };
  const fetchMacros = async () => {
    setLoading(true);
    let query = (supabase as any)
      .from("macros")
      .select(
        `
        id, slug, name, short_desc, risk_score, views_count, screenshot_url,
        public_profiles (username, full_name, verified, avatar_url),
        macro_tags (tags (id, name))
      `,
      )
      .eq("is_published", true)
      .eq("is_approved", true);

    // Apply search filter
    if (searchQuery) {
      query = query.or(`name.ilike.%${searchQuery}%,short_desc.ilike.%${searchQuery}%`);
    }

    // Apply sorting based on active filter
    if (activeFilter === "trending") {
      query = query.order("views_count", {
        ascending: false,
      });
    } else if (activeFilter === "newest") {
      query = query.order("created_at", {
        ascending: false,
      });
    } else {
      query = query.order("views_count", {
        ascending: false,
      });
    }
    query = query.limit(24);
    const { data } = await query;
    
    let filteredData = (data as unknown as Macro[]) || [];

    // Client-side tag filtering
    if (selectedTag) {
      filteredData = filteredData.filter((macro) => macro.macro_tags.some((mt) => mt.tags.name === selectedTag));
    }
    setMacros(filteredData);
    setLoading(false);
  };
  const handleTagClick = (tagName: string) => {
    setSelectedTag(selectedTag === tagName ? null : tagName);
  };
  const handleFilterClick = (filter: string) => {
    setActiveFilter(filter);
  };
  const handleSuggestionClick = (macroName: string) => {
    setSearchQuery(macroName);
    setShowSuggestions(false);
  };

  const startVaultTour = () => {
    const driverObj = driver({
      showProgress: true,
      showButtons: ["next"],
      animate: true,
      smoothScroll: true,
      allowClose: false,
      disableActiveInteraction: false,
      steps: [
        {
          element: "[data-macro-slug]:first-child",
          popover: {
            title: "👋 Welcome to MacroVault!",
            description: "Tap any macro to explore VBA code examples created by the community.",
            side: "right",
            align: "center",
            progressText: "1 of 4",

            onPopoverRender: (popover) => {
              sessionStorage.setItem("continueOnboardingStep", "1");

              // Add custom X button
              const closeBtn = document.createElement("button");
              closeBtn.innerHTML = "×";
              closeBtn.className = `
                absolute top-2 right-2
                w-8 h-8
                flex items-center justify-center
                text-2xl text-gray-400 hover:text-gray-600
                rounded-md hover:bg-gray-100
                transition-colors
                z-10
              `;
              closeBtn.onclick = () => {
                driverObj.destroy();
                markOnboardingComplete();
              };
              popover.wrapper.appendChild(closeBtn);

              const footer = popover.footer;
              footer.innerHTML = ""; // remove Driver.js buttons completely

              // --- Progress text ---
              const progress = document.createElement("div");
              progress.textContent = "1 of 4";
              progress.className = "text-xs text-gray-400 font-normal mb-1";

              // --- Blue CTA Button ---
              const btn = document.createElement("button");
              btn.textContent = "Next";
              btn.className = `
                px-8 py-4
                rounded-xl
                font-semibold text-lg
                bg-primary text-primary-foreground
                hover:bg-primary/90
                shadow-sm
                transition-all
              `;

              btn.onclick = () => {
                const firstMacroCard = document.querySelector("[data-macro-slug]:first-child");
                const href = firstMacroCard?.getAttribute("href");

                sessionStorage.setItem("continueOnboardingStep", "2");
                
                // Add smooth fade-out animation before transition
                popover.wrapper.style.transition = "opacity 0.2s ease-out";
                popover.wrapper.style.opacity = "0";
                
                // Delay navigation to allow fade-out to complete
                setTimeout(() => {
                  driverObj.destroy();
                  if (href) navigate(href);
                }, 200);
              };

              footer.appendChild(progress);
              footer.appendChild(btn);
            },
          },
        },
      ],
    });

    driverObj.drive();
  };
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Macro Vault - Browse Excel VBA Macros</title>
        <meta
          name="description"
          content="Discover and download verified Excel VBA macros for automation, data analysis, and productivity."
        />
        <link rel="canonical" href="https://findmacros.com/vault" />
      </Helmet>
      <Header searchQuery={searchQuery} onSearchChange={setSearchQuery} showSearch={true} />

      {/* Compact Hero Section */}
      <div>
        <div className="px-6 md:px-8 lg:px-12 pt-20 sm:pt-22 lg:pt-24 pb-4">
          <div className="text-center mb-6"></div>

          {/* Categories Grid - Hide when tag is selected */}
          {!selectedTag ? (
            <>
              {/* Mobile: 2 columns */}
              <div className="grid grid-cols-2 gap-8 category-group md:hidden">
                {[0, 1].map((colIndex) => {
                  const tagsPerColumn = Math.ceil(tags.length / 2);
                  const startIdx = colIndex * tagsPerColumn;
                  const endIdx = startIdx + tagsPerColumn;
                  const columnTags = tags.slice(startIdx, endIdx);
                  return (
                    <div key={colIndex} className="space-y-4">
                      {colIndex === 0 && (
                        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1">
                          Categories
                        </h3>
                      )}
                      {colIndex !== 0 && <div className="h-6" />}
                      <div className="space-y-3">
                        {columnTags.map((tag) => (
                          <button
                            key={tag.id}
                            onClick={() => handleTagClick(tag.name)}
                            className={`
                              category-item block w-full text-left text-xl font-bold transition-all duration-300 ease-out
                              hover:scale-105 hover:translate-x-1 origin-left
                              ${selectedTag === tag.name ? "text-foreground" : "text-foreground/90 hover:text-foreground"}
                            `}
                          >
                            {tag.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop: 4 columns */}
              <div className="hidden md:grid grid-cols-4 gap-6 category-group">
                {[0, 1, 2, 3].map((colIndex) => {
                  const tagsPerColumn = Math.ceil(tags.length / 4);
                  const startIdx = colIndex * tagsPerColumn;
                  const endIdx = startIdx + tagsPerColumn;
                  const columnTags = tags.slice(startIdx, endIdx);
                  return (
                    <div key={colIndex} className="space-y-4">
                      {colIndex === 0 && (
                        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1">
                          Categories
                        </h3>
                      )}
                      {colIndex !== 0 && <div className="h-6" />}
                      <div className="space-y-3">
                        {columnTags.map((tag) => (
                          <button
                            key={tag.id}
                            onClick={() => handleTagClick(tag.name)}
                            className={`
                              category-item block w-full text-left text-xl font-bold transition-all duration-300 ease-out
                              hover:scale-105 hover:translate-x-1 origin-left
                              ${selectedTag === tag.name ? "text-foreground" : "text-foreground/90 hover:text-foreground"}
                            `}
                          >
                            {tag.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </> /* Filter Pills - Show when tag is selected */
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setSelectedTag(null)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-foreground bg-background text-foreground font-medium hover:bg-foreground/5 transition-colors"
              >
                {selectedTag}
                <X className="h-4 w-4" />
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border bg-card text-foreground/70 font-medium hover:bg-accent transition-colors">
                    Categories
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48 bg-card z-50">
                  {tags.map((tag) => (
                    <DropdownMenuItem
                      key={tag.id}
                      onClick={() => handleTagClick(tag.name)}
                      className={selectedTag === tag.name ? "bg-accent" : ""}
                    >
                      {tag.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Category description inline */}
              {getCategoryDescription(selectedTag) && (
                <p className="text-sm text-muted-foreground max-w-md">
                  {getCategoryDescription(selectedTag)}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="px-6 md:px-8 lg:px-12 py-12">
        {/* Main Results Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-card rounded-2xl overflow-hidden animate-pulse border border-border/20">
                <div className="aspect-video bg-muted/30" />
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-muted/30 rounded w-3/4" />
                  <div className="h-4 bg-muted/30 rounded w-full" />
                  <div className="h-4 bg-muted/30 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : macros.length === 0 ? (
          <div className="text-center py-20 space-y-4">
            <p className="text-muted-foreground text-lg">No macros found matching your criteria</p>
            {searchQuery && (
              <>
                <p className="text-sm text-muted-foreground">Can't find what you're looking for?</p>
                <Button
                  variant="outline"
                  onClick={() => setShowRequestDialog(true)}
                  className="rounded-full"
                >
                  Request this macro
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {macros.map((macro) => (
                <MacroCard
                  key={macro.id}
                  id={macro.id}
                  slug={macro.slug}
                  name={macro.name}
                  shortDesc={macro.short_desc}
                  ownerHandle={macro.public_profiles?.username || "unknown"}
                  ownerDisplayName={macro.public_profiles?.full_name || macro.public_profiles?.username || "Unknown User"}
                  ownerVerified={macro.public_profiles?.verified || false}
                  riskScore={macro.risk_score}
                  tags={macro.macro_tags.map((mt) => mt.tags.name)}
                  viewsCount={macro.views_count}
                  screenshotUrl={macro.screenshot_url}
                />
            ))}
          </div>
        )}
      </div>

      <RequestMacroDialog
        searchQuery={searchQuery}
        open={showRequestDialog}
        onOpenChange={setShowRequestDialog}
      />
    </div>
  );
};
export default Macros;
